import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, ChevronLeft, ChevronRight, Code, Settings } from 'lucide-react';
import { Button, useAuth } from '@aaas/shared';
import { useSpaceDetailQuery } from '../hooks/useSpaceListQuery';
import { useWidgetListQuery, useWidgetEmbedCodeQuery } from '../hooks';
import { WidgetEmbedCodePanel } from '../components/widget-embed/WidgetEmbedCodePanel';
import WidgetPreviewMini from '../components/widget-association/WidgetPreviewMini';
import type { KMSWidgetConfig } from '../widget/types';
import type { Widget } from '../types/widget.types';

// Helper function to convert Widget to KMSWidgetConfig
const widgetToConfig = (widget: Widget): Partial<KMSWidgetConfig> => ({
  themeColor: widget.theme_color,
  accentColor: widget.accent_color,
  textColor: widget.text_color,
  buttonColor: widget.button_color,
  buttonIconColor: widget.button_icon_color,
  buttonIcon: widget.button_icon,
  buttonText: widget.button_text,
  buttonBorderRadius: widget.button_border_radius,
  position: widget.position as KMSWidgetConfig['position'],
  widgetSize: widget.widget_size,
  widgetHeight: widget.widget_height,
  popupBorderRadius: widget.popup_border_radius,
  headerTitle: widget.header_title,
  botName: widget.bot_name,
  avatarIcon: widget.avatar_icon,
  showPoweredBy: widget.show_powered_by,
  welcomeMessage: widget.welcome_message,
  inputPlaceholder: widget.input_placeholder,
  sendButtonText: widget.send_button_text,
  suggestedQuestions: widget.suggested_questions || [],
  autoOpen: widget.auto_open,
  autoOpenDelay: widget.auto_open_delay,
  persistConversation: widget.persist_conversation,
  maxMessageLength: widget.max_message_length,
  timeout: widget.timeout,
});

const SpaceWidgetEmbedPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { idToken, user, currentWorkspace } = useAuth();
  const [currentIndex, setCurrentIndex] = useState(0);

  // Fetch space details
  const {
    data: space,
    isLoading: spaceLoading,
    error: spaceError,
  } = useSpaceDetailQuery({
    spaceId: id,
    idToken: idToken || undefined,
    enabled: !!id && !!user && !!currentWorkspace,
  });

  // Fetch all widgets
  const { data: widgetResponse, isLoading: widgetsLoading } = useWidgetListQuery({
    skip: 0,
    limit: 100,
    enabled: !!idToken,
  });

  // Filter widgets by space
  const widgets =
    widgetResponse?.items?.filter((widget) => widget.spaces.some((s) => s.id === id)) ?? [];

  const currentWidget = widgets[currentIndex];

  // Fetch embed code for current widget
  const {
    data: embedCode,
    isLoading: embedLoading,
    error: embedError,
  } = useWidgetEmbedCodeQuery({
    widgetId: currentWidget?.id,
    spaceId: id,
    enabled: !!currentWidget && !!id,
  });

  // Navigation handlers
  const handlePrevious = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(widgets.length - 1, prev + 1));
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'Escape') {
        navigate(`/kms/spaces/${id}`);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [widgets.length, id, navigate]);

  // Loading state
  if (spaceLoading || widgetsLoading) {
    return (
      <div className="w-full min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  // Error state
  if (spaceError || !space) {
    return (
      <div className="w-full min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-gray-600">Failed to load space details</p>
          <Button onClick={() => navigate(`/kms/spaces/${id}`)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate(`/kms/spaces/${id}`)}
              className="p-2"
              aria-label="Go back"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                <Code className="w-8 h-8 text-purple-600" />
                Widget Embed Code
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                for <span className="font-semibold">{space.name}</span>
              </p>
            </div>
          </div>
          <div>
            <Button
              variant="outline"
              onClick={() => navigate(`/kms/spaces/${id}/widgets`)}
              className="flex items-center gap-2"
            >
              <Settings className="w-4 h-4" />
              Manage Widgets
            </Button>
          </div>
        </div>

        {/* Main Content */}
        {widgets.length === 0 ? (
          // Empty state - no widgets
          <div className="flex items-center justify-center min-h-[700px]">
            <div className="text-center space-y-6 max-w-md">
              <Sparkles className="w-20 h-20 text-purple-300 mx-auto" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">No Widgets Found</h2>
                <p className="text-gray-600">
                  This space doesn't have any widgets configured yet. Create a widget to get
                  started.
                </p>
              </div>
              <Button
                onClick={() => navigate('/kms/widgets/create')}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Create Widget
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Left Column - Widget Preview */}
            <div className="flex-1 relative">
              {currentWidget && (
                <div
                  className="bg-white rounded-2xl shadow-2xl border-2 border-gray-200 overflow-hidden"
                  style={{ height: '700px' }}
                >
                  <WidgetPreviewMini config={widgetToConfig(currentWidget)} autoOpen={true} />

                  {/* Widget Name Overlay */}
                  <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10">
                    <h3 className="font-semibold text-gray-900 text-base text-center">
                      {currentWidget.name}
                    </h3>
                    {currentWidget.description && (
                      <p className="text-xs text-gray-600 mt-1 text-center line-clamp-1 max-w-md">
                        {currentWidget.description}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              {widgets.length > 1 && (
                <div className="flex items-center justify-center gap-4 mt-4">
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={handlePrevious}
                    disabled={currentIndex === 0}
                    className="rounded-full w-12 h-12 p-0 disabled:opacity-30"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </Button>

                  <div className="px-4 py-2 bg-white rounded-full border-2 border-gray-200 shadow-sm">
                    <span className="text-sm font-semibold text-gray-700">
                      {currentIndex + 1} / {widgets.length}
                    </span>
                  </div>

                  <Button
                    variant="outline"
                    size="lg"
                    onClick={handleNext}
                    disabled={currentIndex === widgets.length - 1}
                    className="rounded-full w-12 h-12 p-0 disabled:opacity-30"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </Button>
                </div>
              )}
            </div>

            {/* Right Column - Embed Code Panel */}
            <div className="flex-1 lg:max-w-md flex-shrink-0">
              <WidgetEmbedCodePanel
                widgetName={currentWidget?.name}
                embedCode={embedCode}
                isLoading={embedLoading}
                error={embedError}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpaceWidgetEmbedPage;
